/**
 * Small controls that sit inside built-in rows (W2-13 settings-keymap): the latency "Calibrate..." button (opens
 * W2-08's calibration dialog, which writes settings.audio.latency.calibrationMs itself), the guided-tour row (W2-14's
 * `tour.start`), and the side effects of the language, keyboard preset and Alt-free rows.
 */

import { DIALOG } from '../../app/dialogs/types';
import type { AppServices } from '../../app/registry/types';
import { Button } from '../../ui';
import { useSettingsValue, useText } from './hooks';
import type { BuiltinRowHooks } from './settingsCatalog';
import { m } from './settings.messages';
import s from './Settings.module.css';

function CalibrateButton(props: { readonly services: AppServices }) {
  const { services } = props;
  const { t } = useText(services);
  const available = services.registry.dialog(DIALOG.latencyCalibration) !== undefined;
  return (
    <Button
      size="sm"
      icon="Timer"
      disabledReason={available ? null : { key: m.calibrateUnavailable }}
      onClick={() => {
        void services.ui.openDialog(DIALOG.latencyCalibration, null).then((result) => {
          if (result !== null) {
            services.ui.toast(
              { key: m.calibrateDone, params: { ms: Math.round(result.calibrationMs) } },
              { kind: 'success' },
            );
          }
        });
      }}
    >
      {t(m.calibrate)}
    </Button>
  );
}

function TourControl(props: { readonly services: AppServices }) {
  const { services } = props;
  const { t } = useText(services);
  const settings = useSettingsValue(services);
  const available = services.registry.command('tour.start') !== undefined;
  return (
    <div className={s.inline}>
      <span className={s.status}>{t(settings.general.tourDone ? m.tourStatusDone : m.tourStatusNotYet)}</span>
      <Button
        size="sm"
        icon="Route"
        disabledReason={available ? null : { key: m.tourUnavailable }}
        onClick={() => {
          void services.registry.run('tour.start', undefined, 'button');
        }}
      >
        {t(settings.general.tourDone ? m.tourAgain : m.tourStart)}
      </Button>
    </div>
  );
}

/** Editing conventions that come with a keymap preset (DESIGN.md §4.2, §8.3). */
export function presetConventions(preset: 'default' | 'rpe'): {
  readonly pasteMode: 'ghost' | 'immediate';
  readonly beatSyntax: 'default' | 'rpe';
  readonly units: 'friendly' | 'rpe';
} {
  return preset === 'rpe'
    ? { pasteMode: 'immediate', beatSyntax: 'rpe', units: 'rpe' }
    : { pasteMode: 'ghost', beatSyntax: 'default', units: 'friendly' };
}

/** True when the editing settings already follow the preset's conventions. */
export function followsConventions(services: Pick<AppServices, 'settings'>, preset: 'default' | 'rpe'): boolean {
  const c = presetConventions(preset);
  const e = services.settings.value.editing;
  return e.pasteMode === c.pasteMode && e.beatSyntax === c.beatSyntax && e.units === c.units;
}

/** Applies the preset's editing conventions (paste mode, beat syntax, units). */
export function applyConventions(services: Pick<AppServices, 'settings'>, preset: 'default' | 'rpe'): void {
  const c = presetConventions(preset);
  services.settings.set('editing', 'pasteMode', c.pasteMode);
  services.settings.set('editing', 'beatSyntax', c.beatSyntax);
  services.settings.set('editing', 'units', c.units);
}

/** Switches the keyboard preset; offers the matching editing conventions when they differ. */
export function switchPreset(services: AppServices, preset: 'default' | 'rpe'): void {
  if (services.keymap.preset !== preset) services.keymap.setPreset(preset);
  if (services.settings.value.keyboard.preset !== preset) services.settings.set('keyboard', 'preset', preset);
  if (!followsConventions(services, preset)) {
    services.ui.toast(
      { key: preset === 'rpe' ? m.conventionsOfferRpe : m.conventionsOfferDefault },
      {
        kind: 'info',
        durationMs: 10_000,
        action: { label: { key: m.conventionsApply }, command: 'settings.applyPresetConventions', args: { preset } },
      },
    );
  }
}

export const ROW_HOOKS: BuiltinRowHooks = {
  calibrate: CalibrateButton,
  tour: TourControl,
  onLocale(services, value) {
    if ((value === 'en' || value === 'zh-CN') && services.i18n.locale !== value) services.i18n.setLocale(value);
  },
  writePreset(services, value) {
    if (value === 'default' || value === 'rpe') switchPreset(services, value);
  },
  writeAltFree(services, value) {
    if (typeof value !== 'boolean') return;
    if (services.keymap.altFree !== value) services.keymap.setAltFree(value);
    if (services.settings.value.keyboard.altFree !== value) services.settings.set('keyboard', 'altFree', value);
  },
};
